import request from './request';

interface IWebSocketTicketResponse {
  ticket?: string;
}

// The ticket-authenticated url of the push websocket connection that is open,
// or being opened. Every instance of the shared connection asks for its url,
// and each ticket gives a url of its own, so handing them all the same one is
// what lets them join a single socket instead of each opening another.
let cachedTicketUrl: { url: string; promise: Promise<string> } | undefined;

// Browsers can't set headers on the websocket handshake, and an embedded
// session has no cookie to send, so the connection authenticates with a
// ticket fetched over a request that carries the session JWT header.
// Tickets are single-use and short-lived: never log the resulting url.
export const getTicketWebSocketUrl = (url: string): Promise<string> => {
  if (cachedTicketUrl?.url !== url) {
    const promise = request<IWebSocketTicketResponse>('/api/websocket/ticket', {
      method: 'POST',
      silent: true,
      persistent: true,
    }).then((response) => {
      if (!response?.ticket) {
        throw new Error('The websocket ticket response has no ticket');
      }

      const ticketUrl = new URL(url);
      ticketUrl.searchParams.set('ticket', response.ticket);
      return ticketUrl.href;
    });

    // Let the next attempt fetch a new ticket rather than reuse the failure.
    promise.catch(() => {
      if (cachedTicketUrl?.promise === promise) {
        cachedTicketUrl = undefined;
      }
    });

    cachedTicketUrl = { url, promise };
  }

  return cachedTicketUrl.promise;
};

// Called once the connection closes: its ticket has been spent, so the
// reconnect, or the next connect, has to fetch a new one.
export const releaseTicketWebSocketUrl = () => {
  cachedTicketUrl = undefined;
};
